// External libraries
// @ts-expect-error untyped-import
import { NON_WHITESPACE } from '@checkout/react-components';
import { getAddressMetadata, getAddressMetaKey } from '../utils';

// Internal modules
import { appStore } from '@/store';
import { getContent } from '../content';
import { ADDRESS_VALIDATION_ERROR, NETWORK_ERRORS } from './constants';
import { features } from './../features';
import { logger } from '@/lib/logger';
import { CountryCodes } from '@/generated/graphql';

const CUSTOM_VALUE_MISSING_FIELDS = new Set([
  'cardCvv',
  'cardExpiry',
  'cardNumber',
  'signupTerms',
  'onboardOption',
]);
const FIELD_NAME_MAP = {
  bankAccountNumber: 'accountNumber',
  bankIban: 'iban',
  bankSortCode: 'sortCode',
  cardCvv: 'cvv',
  cardExpiry: 'expiry',
  phone: 'phoneNumber',
};

/**
 * TODO: When cleaning up the elmo, we should remove these content
 * keys and replace them with the current existing keys we have
 * fieldErrors.firstName.patternMismatch and fieldErrors.valueMissing=This is required.
 */
const JP_NAMES_MAP: Record<string, string> = {
  countrySpecificFirstName: 'fieldErrors.useKanaNames',
  countrySpecificLastName: 'fieldErrors.useKanaNames',
  firstName: 'fieldErrors.useKanjiNames',
  lastName: 'fieldErrors.useKanjiNames',
};

const shouldUseJpKanaAndKanjiErrorCodes = (field: string) => {
  const {
    form: {
      nationality,
      onboardOption: { value: onboardOption },
    },
  } = appStore.getState();

  const isSignup = onboardOption === 'signup';
  return (
    features.isActive('shouldDisplayJpKanaAndKanjiNames') &&
    JP_NAMES_MAP[field] &&
    nationality?.value === CountryCodes.Jp &&
    isSignup
  );
};

const isValueMissing = (value: string) =>
  !value.length || !NON_WHITESPACE.test(value);

const getErrorCodeFromErrorData = (
  errorData: Array<any> | any,
): string | null => {
  if (!Array.isArray(errorData)) {
    // `errorData` sometimes comes back as an object and it can have
    // indices as properties. In that case we'll grab the first error
    // or check to see if the `errorData` has a `code` property on it
    return errorData?.['0']?.code || errorData?.code || null;
  }

  const error = errorData.find(err => err?.code);
  return error?.code;
};

/**
 * @function getValidationMessage
 * @param {string} field | Field id
 * @param {string} value | Current value from the state
 * @param {boolean} isValid | Flag indicating if the field is valid
 * @returns {string}
 * @description Returns an error message to display for an invalid input
 */
const getFieldErrorKey = ({
  field,
  isValid,
  value,
}: {
  field: string;
  isValid: boolean;
  value: string;
}): string => {
  const isCustomValueMissing = CUSTOM_VALUE_MISSING_FIELDS.has(field);
  // @ts-expect-error the field is not required to be on FIELD_NAME_MAP
  const fieldName = FIELD_NAME_MAP[field] || field;

  if (isValueMissing(value)) {
    return `fieldErrors.${isCustomValueMissing ? fieldName : 'value'}Missing`;
  }

  return `fieldErrors.${fieldName}Invalid`;
};

const getFieldError = (data: {
  error?: string;
  errorCode: string;
  field: string;
  isValid: boolean;
  value: string;
}): React.ReactNode[] | string => {
  const { error, errorCode, field, isValid, value } = data;

  if (isValid) {
    return '';
  }

  const { CLIENT_ERROR_CODE, FIELDS } = ADDRESS_VALIDATION_ERROR;
  if (errorCode === CLIENT_ERROR_CODE && FIELDS.includes(field)) {
    return '';
  }

  // We only show the field error message for the password
  // input if the value is missing.
  if (!isValueMissing(value) && field === 'password') {
    return '';
  }

  if (field === 'cardNumber' && errorCode === 'invalidType') {
    const {
      api: { allowedCardTypes },
    } = appStore.getState();
    const supportedCardTypes = allowedCardTypes
      .map(cardType => getContent(`cardTypes.${cardType}`))
      .join(', ');

    return getContent('fieldErrors.cardTypeNotSupported', {
      supportedCardTypes,
    });
  }

  if (field === 'dateOfBirth') {
    const key = errorCode === 'tooYoung' ? 'dobLegalAge' : 'dobInvalidDate';
    return getContent(`fieldErrors.${key}`);
  }

  if (
    field === 'identityDocumentNumber' &&
    features.isActive('isILAMLEnabled')
  ) {
    const { form: formState } = appStore.getState();
    const { value: identityDocumentType } =
      formState.identityDocumentType || {};
    const isNationalId = identityDocumentType === 'NATIONAL_ID';

    if (!isValueMissing(value)) {
      const key = isNationalId ? 'invalidNationalID' : 'invalidPassport';
      return getContent(`fieldErrors.identityDocumentNumber.${key}`);
    }

    logger.info(
      isNationalId ? 'IL_AML_NATIONAL_ID_MISSING' : 'IL_AML_PASSPORT_MISSING',
    );

    const key = isNationalId ? 'nationalIdMissing' : 'passportMissing';
    return getContent(`fieldErrors.common.${key}`);
  }

  // If is Residential Address and the message is from the contingency
  // returns the already existing error message
  if (
    !isValueMissing(value) &&
    errorCode === 'RESIDENTIAL_ADDRESS_INVALID' &&
    error
  ) {
    return error;
  }

  if (!isValueMissing(value) && (field === 'line1' || field === 'line2')) {
    const {
      api: { griffinMetadata = {} },
    } = appStore.getState();
    const { strings } = getAddressMetadata(
      // @ts-expect-error we could prepopulate address or we can optional chain
      // and remove the default
      griffinMetadata.address,
      getAddressMetaKey(),
    );
    const addressLabel = strings[`${field}Label`];
    return getContent(`fieldErrors.${field}Invalid`, {
      addressLabel,
    });
  }

  if (shouldUseJpKanaAndKanjiErrorCodes(field)) {
    return getContent(JP_NAMES_MAP[field]);
  }

  const contentKey = getFieldErrorKey(data);
  return getContent(contentKey);
};

// Checks whether the error is a known network failure, so callers can
// route it to the generic error page instead of the usual hermes/xoon fallback
const isNetworkError = (error: string): boolean => {
  return NETWORK_ERRORS.includes(error);
};

export {
  getErrorCodeFromErrorData,
  getFieldError,
  getFieldErrorKey,
  isNetworkError,
};
